import jp from "jsonpath"
import { getDetailIAScoresByTest, getDetailPTScoresByTest, updateComputerBasedPracticeTestMetaData, updatePTTest } from "../practice-test-scoring/practice-test-service"
import { throttle } from "./throttle"
import { TestAnswer } from "../elearn/practice-test-types"
import { NA } from "../../constants"
import { IACTTestResult, IACTAnswersForQuestions, IAnsweredQuestion } from "../practice-test-scoring/practice-test-summary"
import { capitilizeFirstLetter } from "./text-util"

export const MAX_SCORE_ACT = 36
export const MAX_SCORE_SAT = 1600

export enum TestType {
  ACT = "ACT",
  IA  = "IA",
  SAT = "SAT",
  ENHANCED_ACT = "ENHANCED_ACT",
}

export const ALL_ACT_TESTS_MAP = {
  Diagnostic: "ACT Diagnostic",
  ACT1: "ACT Test 1",
  ACT2: "ACT Test 2",
  ACT3: "ACT Test 3",
  ACT4: "ACT Test 4",
  ACT5: "ACT Test 5",
  ACT6: "ACT Test 6",
  ACT7: "ACT Test 7",
}

export const ALL_SAT_TESTS_MAP = {
  DiagnosticSAT: "SAT Diagnostic",
  SAT1: "SAT Test 1",
  SAT2: "SAT Test 2",
  SAT3: "SAT Test 3",
  SAT4: "SAT Test 4",
  SAT5: "SAT Test 5",
  // SAT6: "SAT Test 6",
  // SAT7: "SAT Test 7",
}

export const ALL_IA_TESTS_MAP = {
  "Pre-Test 2019": "EOY ACT 8th", // 1
  "Practice 2": "BOY ACT 9th",    // 2
  "Pre-Test": "MOY ACT 9th",      // 3  Pre-Test -> MOY ACT 9th (old Pre-Test 2018)
  "Mid-Test": "EOY ACT 9th",      // 4
  "IA 1": "BOY ACT 10th",         // 5
  "Practice 1": "MOY ACT 10th",   // 6
  "IA 3": "EOY ACT 10th",         // 7
  "IA 2": "BOY ACT 11th",         // 8
  "MOY ACT 11th": "MOY ACT 11th", // 9
}

export const ALL_ENHANCED_ACT_TESTS_MAP = {
  EnhancedACTDiagnostic: "Enhanced ACT Diagnostic",
  EnhancedACT1: "Enhanced ACT Test 1",
  // New EACT tests (2, 3 & 4) added on 31 March 2026/Tue
  EnhancedACT2: "Enhanced ACT Test 2",
  EnhancedACT3: "Enhanced ACT Test 3",
  EnhancedACT4: "Enhanced ACT Test 4",
}

export const ACTIVE_TESTS = {
  ACT: Object.keys(ALL_ACT_TESTS_MAP),
  ENHANCED_ACT: Object.keys(ALL_ENHANCED_ACT_TESTS_MAP),
  SAT: Object.keys(ALL_SAT_TESTS_MAP),
  IA:  Object.keys(ALL_IA_TESTS_MAP),
}

export const ALL_TESTS_MAPS = {...ALL_ACT_TESTS_MAP, ...ALL_SAT_TESTS_MAP, ...ALL_IA_TESTS_MAP, ...ALL_ENHANCED_ACT_TESTS_MAP}

// This display order includes both new (Enhanced) as well as the regular (old) ACT tests
export const TEST_DISPLAY_ORDER_ACT = [
  // Enhanced ACTs (new tests)
  "EnhancedACTDiagnostic", 
  "EnhancedACT1",
  "EnhancedACT2",
  "EnhancedACT3",
  "EnhancedACT4",
  "EnhancedACT5",
  "EnhancedACT6",
  "EnhancedACT7",

  // Regular ACTs (old tests)
  "Diagnostic",
  "ACT1",
  "ACT2",
  "ACT3",
  "ACT4",
  "ACT5",
  "ACT6",
  "ACT7",
]

// Display order for SAT tests
export const TEST_DISPLAY_ORDER_SAT = [
  "DiagnosticSAT",
  "SAT1",
  "SAT2",
  "SAT3",
  "SAT4",
  "SAT5",
  "SAT6",
  "SAT7"
]

// Display order for IA tests (check mapping above for the test display names)
export const TEST_DISPLAY_ORDER_IA = [
  "Pre-Test 2019", // 1
  "Practice 2",    // 2
  "Pre-Test",      // 3  Pre-Test -> MOY ACT 9th (old Pre-Test 2018)
  "Mid-Test",      // 4
  "IA 1",          // 5
  "Practice 1",    // 6
  "IA 3",          // 7
  "IA 2",          // 8
  "MOY ACT 11th",  // 9
]

export function getTestTypeDisplayName(testType: string) {
  let testTypeDisplayName = '';
  if (testType === 'act') {
    return 'ACT TESTS';
  } else if (testType === 'enhancedACT' || testType === "enhancedact") {
    return 'ENHANCED ACT TESTS';
  } else if (testType === 'sat') {
    return 'SAT TESTS';
  } else if (testType === 'ia') {
    return 'INTERIM ASSESSMENTS';
  } else {
    return '__default';
  }
}

export function isActTest(testName: string) {
  let keys = Object.keys(ALL_ACT_TESTS_MAP)
  return keys.indexOf(testName) > -1
}

export function isEnhancedActTest(testName: string) {
  let keys = Object.keys(ALL_ENHANCED_ACT_TESTS_MAP)
  return keys.indexOf(testName) > -1
}

/**
 * Checks if this is an Enhanced ACT practice tests based on the testType (case-sensitive)
 * Note that testType might be coming from the url query strign as in the "/practice-tests?testType=act|sat|ia|enhancedACT"
 * 
 * @param testType 
 * @returns 
 */
export function isEnhancedActBasedOnTestType(testType: string) {
  return testType === TestType.ENHANCED_ACT || testType === "enhancedACT"
}

export function isInterimAssessment(testName: string) {
  let keys = Object.keys(ALL_IA_TESTS_MAP)
  return keys.indexOf(testName) > -1
}

export function isSatTest(testName: string) {
  let keys = Object.keys(ALL_SAT_TESTS_MAP)
  return keys.indexOf(testName) > -1
}

/**
 * Gets the test names in a special order.
 * First, diagnostic tests should be displayed at the top for ACT and SAT tests.
 * Second, with the changing display names in the IA tests, ordering of the 
 * testNames should be handled in a special way since the display names now include
 * grade levels. Therefore we need to order them by considering the grade levels too.
 * IA ordering: Grade & {BOY, MOY, EOY} 
 * BOY, MOY, EOY: Beginning, Middle, End of School Year
 * 
 * Note that testNames are used like practice test IDs throughout the application!
 * 
 * Examples: EOY ACT 8th, BOY ACT 10th, MOY 11th, etc.
 * Example of ordered IA tests:
 *  EOY ACT 8th
 *  BOY ACT 10th   
 *  EOY ACT 10th
 *  BOY ACT 11th
 *  MOY ACT 11th
 *  EOY ACT 11th
 * 
 * @param tests 
 * @param testKind 
 * @returns testNames
 */
export function getTestNamesInSpecialOrder(originalTestNames: string[], testKind: "ACT" | "EnhancedACT" | "IA" | "SAT") {
  let testNames = [...originalTestNames] // Clone

  testNames = testNames.sort((n1, n2) => {
    let key = "diagnostic" // Keep the Diagnostic at top
    n1 = n1.toLowerCase()
    n2 = n2.toLowerCase()
    if (n1.includes(key)) return -1
    if (n2.includes(key)) return 1
    if (n1 > n2) return 1
    if (n1 < n2) return -1
    return 0
  }) || []

  if (testKind === "IA") {
    // Force the names into a specific lexicographical order
    let replacersMap = {
      "BOY": "1", // Beginnig of the school year
      "MOY": "2", // Middle of the school year
      "EOY": "3", // End of the school year
    }
    let replace = (testDisplayName: string) => Object.keys(replacersMap).reduce((acc, cur) => acc.replace(cur, `${replacersMap[cur]} ${cur}`), testDisplayName)

    testNames.sort((tn1, tn2) => {
      let tdn1 = replace(getTestDisplayName(tn1))
      let tdn2 = replace(getTestDisplayName(tn2))
      let regex = /\b(\d{1,2})(?:st|nd|rd|th)\b/
      let m1 = tdn1.match(regex)
      let m2 = tdn2.match(regex)      
      let ns1 = m1 ? m1[1].padStart(4, "0"): "" // i.e. 8 -> 0008 for lexicographical ordering
      let ns2 = m2 ? m2[1].padStart(4, "0"): ""      
      let k1 = `${ns1}-${tdn1}`
      let k2 = `${ns2}-${tdn2}`
      if (k1 > k2) return 1
      if (k1 < k2) return -1
      return 0
    })
  }

  return testNames
}


/**
 * Gets the test display name from the testName
 * Note that testNames are used as IDs throughout the application.
 * @param testName 
 * @returns 
 */
export function getTestDisplayName(testName: string): string {
  // These names are identical to the names in Java backend in TestService.java
  let displayName = ALL_TESTS_MAPS[testName] || testName
  
  return displayName
}


export function getTheEnhancedActPdfLink(testName: string, base="/assets/home-pdfs") {
  let link = "?"

  switch (testName) {
    case "EnhancedACTDiagnostic":
      link = `${base}/EnhancedACT_Diagnostic.pdf`
      break
    case "EnhancedACT1":
      link = `${base}/EnhancedACT_Test1.pdf`
      break
    case "EnhancedACT2":
      link = `${base}/EnhancedACT_Test2.pdf`
      break
    case "EnhancedACT3":
      link = `${base}/EnhancedACT_Test3.pdf`
      break
    case "EnhancedACT4":
      link = `${base}/EnhancedACT_Test4.pdf`
      break
    case "EnhancedACT5":
      link = `${base}/EnhancedACT_Test5.pdf`
      break
    case "EnhancedACT6":
      link = `${base}/EnhancedACT_Test6.pdf`
      break
    case "EnhancedACT7":
      link = `${base}/EnhancedACT_Test7.pdf`
      break
  }

  return link
}

/**
 * Gets the short test display name from the testName
 * Short display names are used for the EnhancedACT test because regular display names 
 * are too long for displaying the test display name.
 * 
 * Jennifer's suggestion:
 * "Maybe we drop “enhanced act” and just say “review diagnostic” or “review test 1”"
 * 
 * @param testName 
 * @returns 
 */
export function getShortTestDisplayName(testName: string) {
  // These names are identical to the names in Java backend in TestService.java
  let displayName: string = getTestDisplayName(testName)
  displayName = displayName.replace("Enhanced ACT", "").trim()

  return displayName
}

/**
 * Gets the test type based on the test name.
 * TestName is unique for the PT tests.
 * 
 * @param testName 
 * @returns 
 */
export function getTestTypeBasedOnName(testName: string) {
  if (isActTest(testName)) return TestType.ACT
  if (isSatTest(testName)) return TestType.SAT
  if (isEnhancedActTest(testName)) return TestType.ENHANCED_ACT
  return TestType.IA
}

/**
 * There are special sections in the practice tests in SAT
 * like Writing and Language, Math (Calculator) orMath (No Calculator)
 * so we are just getting the short names for those sections 
 * as categorical names
 * @param practiceTestSection
 * @param isSAT 
 * @returns short categories that corresponds to those sections.
 */
export function getCategoryName(practiceTestSection: string, isSAT: boolean) {
  const categoryMap = {
    "Math (Calculator)": "Math-C",
    "Math (No Calculator)": "Math-NC",
    "Writing and Language": "EBRW-WL",
    "Reading": "EBRW-R"
  }

  if (!isSAT) {
    return practiceTestSection
  }

  return categoryMap[practiceTestSection] ? categoryMap[practiceTestSection]: practiceTestSection
}

export function lookupSection(section: string) {
  let sectionMap = {} as any
  sectionMap.English = 'englishAnswers'
  sectionMap.Math = 'mathAnswers'
  sectionMap.Reading = 'readingAnswers'
  sectionMap.Science = 'scienceAnswers'
  sectionMap['Writing and Language'] = 'writingAndLanguageAnswers'
  sectionMap['Math (No Calculator)'] = 'mathNoCalculatorAnswers'
  sectionMap['Math (Calculator)'] = 'mathCalculatorAnswers'

  // SAT 2024 update
  sectionMap['R&W Module 1'] = 'readingAnswers'
  sectionMap['R&W Module 2'] = 'writingAndLanguageAnswers'
  sectionMap['Math Module 1'] = 'mathNoCalculatorAnswers'
  sectionMap['Math Module 2'] = 'mathCalculatorAnswers'

  return sectionMap[section]
}


// Names for SAT 2024 Update:
// * Reading -> R&W Module 1
// * Writing and Language -> R&W Module 2
// * Math (No Calculator) -> Math Module 1
// * Math (Calculator) -> Math Module 2
export const SAT2024 = {
  DISPLAYNAME_RW_MODULE1: "R&W Module 1",
  DISPLAYNAME_RW_MODULE2: "R&W Module 2",
  DISPLAYNAME_MATH_MODULE1: "Math Module 1",
  DISPLAYNAME_MATH_MODULE2: "Math Module 2",

  // OT: Keep the original sections names and score keys (needed throughout the app)!
  SECTIONNAME_RW_MODULE1: "Reading",
  SECTIONNAME_RW_MODULE2: "Writing and Language",
  SECTIONNAME_MATH_MODULE1: "Math (No Calculator)",
  SECTIONNAME_MATH_MODULE2: "Math (Calculator)",

  SECTIONNAME_CONDENSED_RW_MODULE1: "Reading",
  SECTIONNAME_CONDENSED_RW_MODULE2: "WritingAndLanguage",
  SECTIONNAME_CONDENSED_MATH_MODULE1: "MathNoCalculator",
  SECTIONNAME_CONDENSED_MATH_MODULE2: "MathCalculator",

  SECTIONNAME_SCORE_KEY_RW_MODULE1: "reading",
  SECTIONNAME_SCORE_KEY_RW_MODULE2: "writing & language",
  SECTIONNAME_SCORE_KEY_MATH_MODULE1: "math (no calculator)",
  SECTIONNAME_SCORE_KEY_MATH_MODULE2: "math (calculator)",

}

export function getSATSectionDisplayName(sectionName: string) {
  let displayName = sectionName

  switch (sectionName) {
    case SAT2024.SECTIONNAME_RW_MODULE1:
    case SAT2024.SECTIONNAME_SCORE_KEY_RW_MODULE1:
    case SAT2024.SECTIONNAME_CONDENSED_RW_MODULE1:
      displayName = SAT2024.DISPLAYNAME_RW_MODULE1
      break

    case SAT2024.SECTIONNAME_RW_MODULE2:
    case SAT2024.SECTIONNAME_SCORE_KEY_RW_MODULE2:
    case SAT2024.SECTIONNAME_CONDENSED_RW_MODULE2:
      displayName = SAT2024.DISPLAYNAME_RW_MODULE2
      break

    case SAT2024.SECTIONNAME_MATH_MODULE1:
    case SAT2024.SECTIONNAME_SCORE_KEY_MATH_MODULE1:
    case SAT2024.SECTIONNAME_CONDENSED_MATH_MODULE1:
      displayName = SAT2024.DISPLAYNAME_MATH_MODULE1
      break

    case SAT2024.SECTIONNAME_MATH_MODULE2:
    case SAT2024.SECTIONNAME_SCORE_KEY_MATH_MODULE2:
    case SAT2024.SECTIONNAME_CONDENSED_MATH_MODULE2:
      displayName = SAT2024.DISPLAYNAME_MATH_MODULE2
      break
  }

  return displayName
}

export async function getPracticeTestDetailsByTestId(practiceTestId:string) {
  let testType = getTestTypeBasedOnName(practiceTestId)
  let isIA = isInterimAssessment(practiceTestId)
  let getDetailScoresByTest = await (isIA ? getDetailIAScoresByTest(practiceTestId) : getDetailPTScoresByTest(practiceTestId))

  return getDetailScoresByTest
}

const ACT_MODULE_KEY1 = "englishAnswers"
const ACT_MODULE_KEY2 = "mathAnswers"
const ACT_MODULE_KEY3 = "readingAnswers"
const ACT_MODULE_KEY4 = "scienceAnswers"

export const ACT_TEST_MODULE_KEY_SEQUENCE = [
  ACT_MODULE_KEY1,
  ACT_MODULE_KEY2,
  ACT_MODULE_KEY3,
  ACT_MODULE_KEY4,
]

export const ACT_SECTION_MODULE_KEYS = {
  ENGLISH_MODULE: ACT_MODULE_KEY1,
  MATH_MODULE:    ACT_MODULE_KEY2,
  READING_MODULE: ACT_MODULE_KEY3,
  SCIENCE_MODULE: ACT_MODULE_KEY4,
}

export const ACT_SECTION_MODULE_TITLE = {
  [ACT_MODULE_KEY1]: "Section 1: English",
  [ACT_MODULE_KEY2]: "Section 2: Math",
  [ACT_MODULE_KEY3]: "Section 3: Reading",
  [ACT_MODULE_KEY4]: "Section 4, Science",
}

export type ACTModuleKey = typeof ACT_MODULE_KEY1 |
                           typeof ACT_MODULE_KEY2 |
                           typeof ACT_MODULE_KEY3 |
                           typeof ACT_MODULE_KEY4


export const ACT_SUBJECT_MAP: Record<ACTModuleKey, string> = {
  [ACT_MODULE_KEY1]: "english",
  [ACT_MODULE_KEY2]: "math",
  [ACT_MODULE_KEY3]: "reading",
  [ACT_MODULE_KEY4]: "science",
}

const SAT_MODULE_KEY1 = "readingAnswers"
const SAT_MODULE_KEY2 = "writingAndLanguageAnswers"
const SAT_MODULE_KEY3 = "mathNoCalculatorAnswers"
const SAT_MODULE_KEY4 = "mathCalculatorAnswers"

export type SATModuleKey = typeof SAT_MODULE_KEY1 |
                           typeof SAT_MODULE_KEY2 |
                           typeof SAT_MODULE_KEY3 |
                           typeof SAT_MODULE_KEY4

/**
 * SAT has two sections: Read & Write and Math
 * Each sections has two modules
 */
export const SAT_SECTION_MODULE_KEYS = {
  RW_MODULE1:   SAT_MODULE_KEY1,
  RW_MODULE2:   SAT_MODULE_KEY2,
  MATH_MODULE1: SAT_MODULE_KEY3,
  MATH_MODULE2: SAT_MODULE_KEY4,
}

export const SAT_SECTION_MODULE_TITLE: Record<SATModuleKey, string> = {
  [SAT_MODULE_KEY1]: "Section 1, Module 1: Reading and Writing",
  [SAT_MODULE_KEY2]: "Section 1, Module 2: Reading and Writing",
  [SAT_MODULE_KEY3]: "Section 2, Module 1: Math",
  [SAT_MODULE_KEY4]: "Section 2, Module 2: Math",
}

export const SAT_SUBJECT_MAP: Record<SATModuleKey, string> = {
  [SAT_MODULE_KEY1]: "reading",
  [SAT_MODULE_KEY2]: "writing and language",
  [SAT_MODULE_KEY3]: "math (no calculator)",
  [SAT_MODULE_KEY4]: "math (calculator)",
}

export const SAT_TEST_MODULE_KEY_SEQUENCE = [
  SAT_MODULE_KEY1,
  SAT_MODULE_KEY2,
  SAT_MODULE_KEY3,
  SAT_MODULE_KEY4,
]

// SAT Test Times (Duration)
export const SAT_REGULAR_TIME_IN_MINUTES_RW   = 39
export const SAT_REGULAR_TIME_IN_MINUTES_MATH = 43
export const SAT_BREAK_TIME_IN_MINUTES        = 10
export const SAT_BREAK_TIME_IN_SECONDS        = SAT_BREAK_TIME_IN_MINUTES*60

// ACT Test Times (Duration)
export const ACT_REGULAR_TIME_IN_MINUTES_ENGLISH = 35
export const ACT_REGULAR_TIME_IN_MINUTES_MATH    = 50
export const ACT_REGULAR_TIME_IN_MINUTES_READING = 40
export const ACT_REGULAR_TIME_IN_MINUTES_SCIENCE = 40
export const ACT_BREAK_TIME_IN_MINUTES           = 10
export const ACT_BREAK_TIME_IN_SECONDS           = ACT_BREAK_TIME_IN_MINUTES*60


export type TestTimeIndicator = "regular" | "extended1" | "extended2" | "extended3"


/**
 * Gets the section info based on the module which is used in the computer-based SAT virtual tests
 * 
 * @param moduleNameKey 
 * @param details 
 * @returns 
 */
export function getSectionInfoSAT(moduleNameKey: SATModuleKey|string, details) {
  let sectionTitle = SAT_SECTION_MODULE_TITLE[moduleNameKey]
  let {displayName} = details
  let testTitle = `${displayName} Practice Test`
  let testTimeInMinutes = moduleNameKey.includes("math") ? SAT_REGULAR_TIME_IN_MINUTES_MATH: SAT_REGULAR_TIME_IN_MINUTES_RW
  let testTimeInSeconds = testTimeInMinutes*60
  let breakTimeInseconds = SAT_BREAK_TIME_IN_MINUTES*60
  let testModuleType = moduleNameKey.includes("math") ? "math" : "rw"
  let computerTestCapabilities = {
    testModuleType,
    calculator: testModuleType === "math",
    reference: testModuleType === "math",
    highlight: testModuleType !== "math",
    linereader: true,
    testDurationInSeconds: {
      regular: testTimeInSeconds,
      extended1: Math.ceil(1.5*testTimeInSeconds),
      extended2: 2*testTimeInSeconds,
      break: breakTimeInseconds,
    },
    minimumResolution: {
      width: 1024,
      height: 768
    },
    sequence: SAT_TEST_MODULE_KEY_SEQUENCE
  }

  return {sectionTitle, testTitle, displayName, computerTestCapabilities}
}

/**
 * Gets the regular times in minutes based on the test module.
 * 
 * @param moduleNameKey 
 * @returns 
 */
function getRegularTestTimeInMinutesACT(moduleNameKey: string) {
  let timeInMinutes = -1

  switch (moduleNameKey) {
    case ACT_MODULE_KEY1:
      timeInMinutes = ACT_REGULAR_TIME_IN_MINUTES_ENGLISH
      break
    case ACT_MODULE_KEY2:
      timeInMinutes = ACT_REGULAR_TIME_IN_MINUTES_MATH
      break
    case ACT_MODULE_KEY3:
      timeInMinutes = ACT_REGULAR_TIME_IN_MINUTES_READING
      break
    case ACT_MODULE_KEY4:
      timeInMinutes = ACT_REGULAR_TIME_IN_MINUTES_SCIENCE
      break
  }
  return timeInMinutes
}

/**
 * Gets the section info based on the module which is used in the computer-based ACT virtual tests (aka digital tests)
 * 
 * @param moduleNameKey 
 * @param details 
 * @returns 
 */
export function getSectionInfoACT(moduleNameKey: SATModuleKey|string, details) {
  let sectionTitle = ACT_SECTION_MODULE_TITLE[moduleNameKey]
  let {displayName} = details
  let testTitle = `${displayName} Practice Test`
  let testTimeInMinutes = getRegularTestTimeInMinutesACT(moduleNameKey)
  let testTimeInSeconds = testTimeInMinutes*60
  let breakTimeInseconds = ACT_BREAK_TIME_IN_MINUTES*60
  let testModuleType = moduleNameKey.includes("math") ? "math" : "rw"
  let computerTestCapabilities = {
    testModuleType,
    calculator: testModuleType === "math",
    reference: testModuleType === "math",
    highlight: testModuleType !== "math",
    linereader: true,
    testDurationInSeconds: {
      regular: testTimeInSeconds,
      extended1: Math.ceil(1.5*testTimeInSeconds),
      extended2: 2*testTimeInSeconds,
      extended3: 3*testTimeInSeconds,
      break: breakTimeInseconds,
    },
    minimumResolution: {
      width: 1024,
      height: 768
    },
    sequence: ACT_TEST_MODULE_KEY_SEQUENCE
  }

  return {sectionTitle, testTitle, displayName, computerTestCapabilities}
}

/**
 * Gets the ACT break time
 * @param testDetails 
 * @returns 
 */
export function getTestBreakTimeACT(testDetails) {
  let {testTimeIndicator} = testDetails
  let breakTimeInSeconds = ACT_BREAK_TIME_IN_SECONDS // For regular
  let muliplier = 1 // regular

  switch (testTimeIndicator) {
    case "extended1": 
      muliplier = 1.5
      break

    case "extended2": 
      muliplier = 2
      break

    case "extended3": 
      muliplier = 3
      break
  }

  breakTimeInSeconds *= muliplier

  return breakTimeInSeconds
}

interface IPracticeTestStatus {
  details: any
  computerTestEnabled: boolean
  computerBasedTest: boolean
  userEntriesFound: boolean
  createdTime: string
  startedTime: string,
  pausedTime: string,
  completedTime: string
}

/**
 * Gets the status of the practice test: Determines if the test started, any user entries exists, etc.
 */
export async function getPracticeTestStatus(testName: string): Promise<IPracticeTestStatus> {
  let details = await getPracticeTestDetailsByTestId(testName)
  let {questions, computerBasedTest, createdTime, startedTime, pausedTime, completedTime} = details
  let computerTestEnabled = computerBasedTest
  let jpQueryEntires = `$..userSubmittedAnswer`
  let jpEntriesRes = jp.query(questions, jpQueryEntires).filter(r => r)
  let userEntriesFound = jpEntriesRes && jpEntriesRes.length > 0 ? true: false

  if (!computerTestEnabled) {
    // Check if this test capable for computer based test
    let jpQuery = `$..choiceA`
    let jpRes = jp.query(questions, jpQuery).filter(r => r)
    let computerQuestionsFound = jpRes && jpRes.length > 0 ? true: false

    if (computerQuestionsFound && !userEntriesFound) {
      computerTestEnabled = true
    }
  }

  let practiceTestStatus = {
    details,
    computerBasedTest,
    computerTestEnabled,
    userEntriesFound,
    createdTime, 
    startedTime,
    pausedTime,
    completedTime,
  }

  return practiceTestStatus
}

/**
 * Gets the initial suggested navigation link for the practice test.
 * It is used to either navigate to a link directly or 
 * show a selections dialog for Paper Test or Computer-based Test.
 * 
 * Important checks:
 * User Entries (UE)
 * Computer Enabled (CE)
 * Completed DateTime (CD)
 * 
 * UE | CE | CD | Action
 * ---------------------
 *  0 |  0 |  0 | Paper Test Instructions (Take)
 *  0 |  0 |  1 | Paper Test Instructions (Take) (shouldn't happen!)
 *  0 |  1 |  0 | Selections: Paper Test (Take) | Computer Test (Take)
 *  0 |  1 |  1 | Paper Test (Review) Computer Test cannot be taken (needs reset by admin)
 *  1 |  0 |  0 | Paper Test (Review)
 *  1 |  0 |  1 | Paper Test (Review)
 *  1 |  1 |  0 | Computer Test (Continue)
 *  1 |  1 |  1 | Paper Test (Review)
 */
export async function getSuggestedNavigationLinkForPracticeTest(testName: string): Promise<string|undefined> {
  let status = await getPracticeTestStatus(testName)
  let {userEntriesFound, computerTestEnabled, completedTime} = status
  let link

  // Consolidated actions!
  if (!userEntriesFound && !computerTestEnabled) {
    // Paper Test Instructions (Take)
    link = `/app/practice-tests/instructions/${testName}`
  }
  else if (!userEntriesFound && computerTestEnabled) {
    // Selections: Paper Test (Take) | Computer Test (Take)
    // Notice that no link set!
  }
  else if (!userEntriesFound && computerTestEnabled && completedTime) {
    // Paper Test (Review) Computer Test cannot be taken (needs reset by admin)
    link = `/app/practice-tests/scoring/${testName}`
  }
  else if (userEntriesFound && computerTestEnabled && !completedTime) {
    // Computer Test (Continue)
    link = `/app/practice-tests/virtual/${testName}`
  }
  else {
    // Paper Test (Review)
    link = `/app/practice-tests/scoring/${testName}`
  }

  return link
}


//--- Save Computer-based Virtual Test Answers ---

/**
 * Saves the user's answers to the backend
 * @param testDetails 
 */
// export async function _saveAllComputerBasedTestAnswers(testDetails) {
export async function _saveAllComputerBasedTestAnswers(testDetails) {
  let {remainingTimeInSeconds, currentQuestionIndex, currentModuleNameKey, testTimeIndicator, completedTime} = testDetails
  let {testName} = testDetails

  updateComputerBasedPracticeTestMetaData({
    testName,
    completedTime,
    currentModuleNameKey,
    testTimeIndicator,
    remainingTimeInSeconds,
    currentQuestionIndex,
  })
}

export const saveAllComputerBasedTestAnswers = throttle(_saveAllComputerBasedTestAnswers, 1000)

// Save test answer for SAT
export async function saveComputerBasedTestAnswer(testName: string, moduleNameKey: string, answeredQuestion) {
  let testSubject = SAT_SUBJECT_MAP[moduleNameKey]
  let {correctAnswer, userSubmittedAnswer, bookmarked, questionNumber, fillInAnswer} = answeredQuestion
  let isCorrect = correctAnswer === userSubmittedAnswer
  let testAnswer: TestAnswer = {
    mcLetter: userSubmittedAnswer ? userSubmittedAnswer: "?",
    isCorrect,
    bookmarked,
    fillInAnswer,
  }

  updatePTTest(testName, testSubject, questionNumber, testAnswer)
}

// Save test answer for ACT
export async function saveComputerBasedTestAnswerACT(testName: string, moduleNameKey: string, answeredQuestion) {
  let testSubject = ACT_SUBJECT_MAP[moduleNameKey]
  let {correctAnswer, userSubmittedAnswer, bookmarked, questionNumber, fillInAnswer} = answeredQuestion
  let isCorrect = correctAnswer === userSubmittedAnswer
  let testAnswer: TestAnswer = {
    mcLetter: userSubmittedAnswer ? userSubmittedAnswer: "?",
    isCorrect,
    bookmarked,
    fillInAnswer,
  }

  updatePTTest(testName, testSubject, questionNumber, testAnswer)
}

export async function saveComputerBasedTestFillInAnswer(testName: string, moduleNameKey: string, answeredQuestion) {
  let {userSubmittedAnswer, acceptableAnswers, fillInAnswer} = answeredQuestion
  let allPossibleAnswers = acceptableAnswers.split(",").map(v => v.trim())
  let foundAnswer = allPossibleAnswers.find(possibleAnswer => possibleAnswer === fillInAnswer)

  if (foundAnswer) {
    userSubmittedAnswer = "Y"
  }
  else {
    userSubmittedAnswer = "N"
  }

  answeredQuestion.userSubmittedAnswer = userSubmittedAnswer

  saveComputerBasedTestAnswer(testName, moduleNameKey, answeredQuestion)
}

// export async function saveUserBookmark(testName, moduleNameKey, bookmarkedQuestion) {
//   saveComputerBasedTestAnswer(testName, moduleNameKey, bookmarkedQuestion)
// }

// export async function saveModuleComputerBasedAnswers(args: {testDetails: any, currentModuleNameKey: string, currentQuestionIndex: number, remainingTimeInSeconds: number}) {
//   // Module ended, we need to find the next module info and reset the remaining time!
//   let {testDetails} = args
//   let {remainingTimeInSeconds, currentQuestionIndex, currentModuleNameKey, testTimeIndicator, completedTime} = testDetails
//   let {testName} = testDetails

//   updateComputerBasedPracticeTestMetaData({
//     testName,
//     completedTime,
//     currentModuleNameKey,
//     testTimeIndicator,
//     remainingTimeInSeconds,
//     currentQuestionIndex,
//   })
// }

export async function saveHighlightForQuestion(highlightedQuestion) {
}

async function _updateRemainingTimeForModule(args: {currentModuleNameKey: string, currentQuestionIndex: number, remainingTimeInSeconds: number}) {
  let {currentModuleNameKey, currentQuestionIndex, remainingTimeInSeconds} = args
}

export const updateRemainingTimeForModule = throttle(_updateRemainingTimeForModule, 5_000)

export function findNextModuleNameKeyForSAT(currentModuleNameKey: string): string {
  let foundIndex = SAT_TEST_MODULE_KEY_SEQUENCE.findIndex(mk => mk === currentModuleNameKey)
  let totalModules = SAT_TEST_MODULE_KEY_SEQUENCE.length
  let nextIndex = foundIndex + 1
  let nextModuleNameKey = nextIndex < totalModules ? SAT_TEST_MODULE_KEY_SEQUENCE[nextIndex]: NA
  return nextModuleNameKey
}

export function findNextModuleNameKeyForACT(currentModuleNameKey: string): string {
  let foundIndex = ACT_TEST_MODULE_KEY_SEQUENCE.findIndex(mk => mk === currentModuleNameKey)
  let totalModules = ACT_TEST_MODULE_KEY_SEQUENCE.length
  let nextIndex = foundIndex + 1
  let nextModuleNameKey = nextIndex < totalModules ? ACT_TEST_MODULE_KEY_SEQUENCE[nextIndex]: NA
  return nextModuleNameKey
}

export function saveTestStartTime(testDetails) {
  let {testName} = testDetails
  let startedTime = new Date().toISOString()
  updateComputerBasedPracticeTestMetaData({
    testName,
    startedTime,
  })
}

export const ACT_ANSWER_SECTIONS = [ACT_MODULE_KEY1, ACT_MODULE_KEY2, ACT_MODULE_KEY3, ACT_MODULE_KEY4]
export type ActAnswerSection = typeof ACT_ANSWER_SECTIONS[number]

/**
 * Gets the ACT subject name (i.e. English, Math, Reading, Science)
 * @param sectionKey (aka moduleKey)
 * @returns 
 */
export function getACTTestSubjectNameByAnswerSection(sectionKey: ActAnswerSection): string {
  let testSubject = ACT_SUBJECT_MAP[sectionKey]
  if (!testSubject) return "Unknown Subject"
  return capitilizeFirstLetter(testSubject)
}

/**
 * This function populates the missing questionText and questionBox for all the 
 * questions in the same group.
 * 
 * The reason for this is that many questions in Enhanced ACT are grouped by
 * questionGroup, and the questionText and questionBox are only provided for 
 * one question in the group!
 * 
 * @param actPracticeTest
 * @returns actPracticeTest with populated questionText and questionBox
 */
export function populateEnhancedActPracticeTestQuestions(actPracticeTest: IACTTestResult) { 
  let allQuestions: IACTAnswersForQuestions = actPracticeTest.questions

  ACT_ANSWER_SECTIONS.forEach(section => {
    let questions = allQuestions[section] as IAnsweredQuestion[]

    if (!questions) return

    let questionGroups = questions.map(q => {
        return {
            questionGroup: q.questionGroup,
            questionText: q.questionText,
            questionBox: q.questionBox,
        }
      })
      .filter(q => q.questionGroup && !!q.questionText)

    let updatedQuestions = questions.map(question => {
      if (question.questionGroup && (!question.questionText || !question.questionBox)) {
        let group = questionGroups.find(questionGroup => questionGroup.questionGroup === question.questionGroup)
        if (question && group) {
          if (!question.questionText) question.questionText = group.questionText
          if (!question.questionBox) question.questionBox = group.questionBox
        }
      }
      question.section = section
      question.subjectName = getACTTestSubjectNameByAnswerSection(section)

      return question
    })

    allQuestions[section] = updatedQuestions
  })

  return actPracticeTest
}
